import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';

const Wrapper = styled.section`
  background-color: ${brand.red.base};
  padding: 30px 0 20px;
`;

const Row = styled.div`
  margin-bottom: 20px;
  text-align: center;

  @media ${from(Device.TabletLarge)} {
    text-align: unset;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
  }

  small {
    display: block;
    color: ${brand.white};
    font-size: 12px;
    line-height: 1.4;
    margin: 0 auto 24px;

    @media ${from(Device.TabletLarge)} {
      margin: unset;
      font-size: 14px;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;

  @media ${until(Device.TabletLarge)} {
    margin-bottom: 20px;
  }
`;

const SocialIcon = styled.a`
  opacity: 0.8;
  transition: opacity 300ms ease-out;

  &:hover {
    opacity: 1;
  }

  span {
    position: absolute;
    opacity: 0;
    visibility: hidden;
    height: 0;
    width: 0;
    text-indent: -9999px;
    overflow: hidden;
    pointer-events: none;
  }

  svg {
    max-height: 30px;
    max-width: 30px;
    height: 100%;
    width: 100%;
  }
`;

const CultureWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;

  @media ${from(Device.TabletLarge)} {
    justify-content: flex-end;
  }
`;

const CultureSelect = styled.div<{ active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  width: 80px;
  padding: 0 12px;
  background-color: ${brand.white};
  cursor: pointer;

  &:hover:after {
    transform: scaleX(1.08);
  }

  ${({ active }) =>
    active &&
    css`
      &:after {
        transform: scaleX(1);
      }

      &:hover:after {
        transform: scaleX(1);
      }
    `}

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${brand.white};
    transform-origin: center center;
    transition: transform 275ms ease-out;
  }

  ${() => Icon}, svg {
    position: relative;
    z-index: 1;
  }
`;

const Icon = styled(m.div)`
  display: inline-flex;
  align-items: center;
  width: 16px;
  height: 16px;
  transform-origin: center center;

  svg {
    width: 16px;
    height: 16px;
    color: ${brand.red.base};
  }
`;

const CultureList = styled.div<{ visible: boolean }>`
  position: absolute;
  bottom: 100%;
  right: 50%;
  padding: 20px;
  background-color: ${brand.white};
  opacity: 0;
  transform: scale(0.2) translateX(50%);
  transform-origin: bottom right;
  pointer-events: none;
  transition: opacity 275ms ease-out, transform 275ms ease-out;

  ${({ visible }) =>
    visible &&
    css`
      opacity: 1;
      transform: scale(1) translateX(50%);
      pointer-events: all;
    `}

  @media ${until(Device.TabletLarge)} {
    width: 100%;
    height: 300px;
    overflow-y: scroll;
    text-align: left;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  }

  @media ${until(Device.MobileLarge)} {
    width: 80%;
  }

  @media ${from(Device.TabletLarge)} {
    right: 0;
    transform: scale(0.2);
    ${({ visible }) =>
      visible &&
      css`
        transform: scale(1);
      `}
  }
`;

const CultureListInner = styled.ul`
  column-count: 1;

  @media ${from(Device.MobileLarge)} {
    column-count: 2;
  }
`;

const CultureItem = styled.li`
  list-style: none;

  @media ${from(Device.TabletLarge)} {
    display: inline-block;
    min-width: 280px;
  }
`;

const CultureLink = styled.a`
  display: flex;
  align-items: center;
  ${fonts.helvetica['53-extended']};
  font-size: 14px;
  color: ${brand.red.base};

  &:hover {
    color: ${brand.red.base};
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
    margin-right: 10px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media ${from(Device.MobileLarge)} {
    flex-direction: row;
    flex-wrap: wrap;
  }

  @media ${from(Device.TabletLarge)} {
    justify-content: flex-start;
  }
`;

const Item = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;

  &:last-child {
    padding-bottom: unset;

    &:after {
      display: none;
    }
  }

  @media ${from(Device.MobileLarge)} {
    padding-bottom: unset;
    margin-right: 10px;
    padding-right: 10px;
    border-right: 1px solid rgba(255 255 255 / 60%);

    &:last-child {
      border-right: unset;
    }
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    display: block;
    height: 1px;
    width: 4px;
    border-bottom: 1px solid rgba(255 255 255 / 60%);

    @media ${from(Device.MobileLarge)} {
      display: none;
    }
  }
`;

const Link = styled.a`
  color: ${brand.white};
  ${fonts.helvetica['53-extended']};
  font-size: 12px;
  line-height: 1.2;

  &:hover {
    color: ${brand.white};
    text-decoration: underline;
  }

  &#ot-sdk-btn.ot-sdk-show-settings {
    background: none;
    border: none;
    padding: unset;
    margin-left: unset;
    color: ${brand.white};
    font-size: 12px;
    line-height: 1.2;
  }
`;

export default {
  Wrapper,
  Row,
  SocialLinks,
  SocialIcon,
  CultureWrapper,
  CultureSelect,
  Icon,
  CultureList,
  CultureListInner,
  CultureItem,
  CultureLink,
  List,
  Item,
  Link,
};
